import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PiInfo, PiMoney } from 'react-icons/pi';
import DocumentsUpload from '@/components/DocumentsUpload/DocumentsUpload';
import { DocumentType, PaymentMethod, TInvoice } from '@/types';

interface PaymentConfirmationFormProps {
  invoice: TInvoice;
  opened: boolean;
  onClose: () => void;
  onSubmit?: (data: PaymentConfirmationData) => Promise<void>;
}

export interface PaymentConfirmationData {
  invoiceId: string;
  paymentMethod: PaymentMethod;
  transactionHash?: string;
  fileId?: string;
  notes?: string;
}

interface PaymentFormValues {
  transactionHash: string;
  notes: string;
}

export function PaymentConfirmationForm({
  invoice,
  opened,
  onClose,
  onSubmit,
}: PaymentConfirmationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PaymentFormValues>({
    initialValues: {
      transactionHash: '',
      notes: '',
    },
    validate: {
      transactionHash: (value) => {
        if (
          invoice.paymentMethod === PaymentMethod.Cryptocurrency &&
          !value.trim()
        ) {
          return 'Transaction hash is required for cryptocurrency payments';
        }
        return null;
      },
    },
  });

  if (!invoice) {
    return null;
  }

  // The proforma's method as it stands now: an amendment can change it after the form was first made.
  const requiresHash = invoice.paymentMethod === PaymentMethod.Cryptocurrency;
  const requiresFile = !requiresHash;

  const handleSubmit = async (values: PaymentFormValues, fileId?: string) => {
    setIsSubmitting(true);

    try {
      const data: PaymentConfirmationData = {
        invoiceId: invoice.id.toString(),
        paymentMethod: invoice.paymentMethod as PaymentMethod,
        notes: values.notes,
      };

      if (requiresHash) {
        data.transactionHash = values.transactionHash.trim();
      }

      if (fileId) {
        data.fileId = fileId;
      }

      await onSubmit?.(data);

      notifications.show({
        message: 'Payment confirmation added',
        color: 'green',
      });

      onClose();
      form.reset();
    } catch (_error) {
      notifications.show({
        message:
          'The report was not added. Try again: a retry never adds it twice.',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <PiMoney size={18} />
          <Text size="md" fw={600}>
            Add payment confirmation
          </Text>
        </Group>
      }
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
        <Stack gap="md">
          <Alert color="blue" icon={<PiInfo />}>
            <Text size="sm">
              Record the payment confirmation for proforma{' '}
              {invoice.invoiceNumber}. A confirmation is a claim to check
              against the bank or the chain; it never changes the state or the
              amount collected. Earlier confirmations are kept.
            </Text>
          </Alert>

          <TextInput
            label="Note"
            placeholder="Optional, for example who sent the proof"
            maxLength={1000}
            {...form.getInputProps('notes')}
          />

          {requiresHash && (
            <Stack>
              <TextInput
                label="Transaction Hash"
                placeholder="Enter cryptocurrency transaction hash"
                {...form.getInputProps('transactionHash')}
                required
                styles={{ input: { fontFamily: 'monospace' } }}
                description="Provide the transaction hash from your cryptocurrency wallet"
              />
              <Button type="submit">Save</Button>
            </Stack>
          )}

          {requiresFile && (
            <div>
              <Text size="sm" fw={500} mb="xs">
                Upload proof of payment
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                Upload a document that proves payment was made (receipt, bank
                statement, etc.)
              </Text>
              <DocumentsUpload
                type={DocumentType.PaymentConfirmation}
                pulseId={invoice.contactId}
                maxDocuments={1}
                onUploadSuccess={async (documents) => {
                  if (documents.length > 0) {
                    await handleSubmit(form.values, documents[0].id.toString());
                  }
                }}
              />
            </div>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export default PaymentConfirmationForm;
